'use client'

import { AlertCircle } from 'lucide-react'
import { type PropsWithChildren, type ReactNode, useMemo } from 'react'
import {
  LoadingType,
  StepCircle,
} from '@/src/components/route-details/step-circle'
import { StepLine } from '@/src/components/route-details/step-line'
import { cn } from '@/src/utils/styles'

interface TitleProps {
  isError?: boolean
  title: string
}

export const BasicStepTitle = ({ isError, title }: TitleProps) => {
  return (
    <h3
      className={cn('text-base text-link font-semibold', {
        'text-danger-text': isError,
      })}
    >
      {title}
    </h3>
  )
}

interface BasicStepProps {
  className?: string
  isActive?: boolean
  isError?: boolean
  isLoading?: boolean
  isSuccess?: boolean
  isWaiting?: boolean
  isWarning?: boolean
  title: ReactNode
  transferDetails?: {
    startedAtMs: number
    estimatedTimeMs: number
  }
}

export const BasicStep = ({
  className,
  isActive,
  isError,
  isLoading,
  isSuccess,
  isWaiting,
  isWarning,
  transferDetails,
  title,
  children,
}: PropsWithChildren<BasicStepProps>) => {
  const loadingType = useMemo(() => {
    if (isWaiting) return LoadingType.Pulse
    if (isLoading && transferDetails) return LoadingType.Progress
    if (isLoading) return LoadingType.Spinner
    return undefined
  }, [isWaiting, isLoading, transferDetails])

  return (
    <li className={cn('flex relative pr-5 min-h-full pt-10 pb-5', className)}>
      <div className='flex gap-4 w-full'>
        <div className='max-h-full pt-1'>
          <div className='relative flex flex-col items-center'>
            <StepLine
              isActive={isActive}
              isSuccess={isSuccess}
              isError={isError}
              className='absolute bottom-trade-flow-line-offset h-trade-flow-line-height'
            />
            {isWarning ? (
              <AlertCircle
                width={24}
                height={24}
                className='text-danger-text'
              />
            ) : (
              <StepCircle
                isSuccess={isSuccess}
                isError={isError}
                isLoading={isLoading}
                className='min-h-6'
                type={loadingType}
                transferDetails={transferDetails}
              />
            )}
          </div>
        </div>

        <div className='flex justify-between w-full'>
          <div className='flex flex-col gap-1 w-full'>
            <div className='flex flex-row items-center w-full gap-2'>
              {typeof title === 'string' ? (
                <BasicStepTitle isError={isError || isWarning} title={title} />
              ) : (
                title
              )}
            </div>

            <div className='flex flex-col text-left'>{children}</div>
          </div>
        </div>
      </div>
    </li>
  )
}
